import React from 'react';
import { useApiClient } from '../../api/client';
import { StatusBadge } from '../ui';

type DocumentType = 'ID_CARD' | 'PASSPORT' | 'DRIVING_LICENSE' | 'RESIDENCE_PERMIT';
type Document = {
  id: string; provider: string; documentType: string; status: string;
  expirationDate: string | null; recordedBy: string | null; recordedAt: string | null;
};
const documentLabels: Record<DocumentType,string> = {ID_CARD:'Personalausweis',PASSPORT:'Reisepass',DRIVING_LICENSE:'Führerschein',RESIDENCE_PERMIT:'Aufenthaltstitel'};
const isoToday = () => new Intl.DateTimeFormat('en-CA',{timeZone:'Europe/Vienna',year:'numeric',month:'2-digit',day:'2-digit'}).format(new Date());

export function AdminUserVerificationTab({user,onSaved}:{
  user:{id:string;deletedAt?:string|null};onSaved:()=>Promise<void>;
}) {
  const {request}=useApiClient();
  const [documents,setDocuments]=React.useState<Document[]>([]);
  const [loading,setLoading]=React.useState(true);
  const [loadError,setLoadError]=React.useState('');
  const [error,setError]=React.useState('');
  const [success,setSuccess]=React.useState('');
  const [open,setOpen]=React.useState(false);
  const [busy,setBusy]=React.useState(false);
  const [documentType,setDocumentType]=React.useState<DocumentType>('ID_CARD');
  const [expirationDate,setExpirationDate]=React.useState('');
  const alive=React.useRef(false);
  const path=`/admin/users/${encodeURIComponent(user.id)}/verification-documents`;
  const load=React.useCallback(async()=>{
    setLoading(true);setLoadError('');
    try {const next=await request<Document[]>(path);if(alive.current)setDocuments(next);}
    catch(cause){if(alive.current)setLoadError(cause instanceof Error?cause.message:'Die Verifizierungen konnten nicht geladen werden.');}
    finally{if(alive.current)setLoading(false);}
  },[request,path]);
  React.useEffect(()=>{alive.current=true;void load();return()=>{alive.current=false;};},[load]);
  async function save(event:React.FormEvent<HTMLFormElement>) {
    event.preventDefault();if(busy)return;
    setBusy(true);setError('');setSuccess('');
    try {
      const saved=await request<Document>(path,{method:'POST',body:JSON.stringify({documentType,expirationDate})});
      if(alive.current){
        setDocuments(current=>[saved,...current]);setOpen(false);setExpirationDate('');
        setSuccess('Manuelle Verifizierung gespeichert.');
      }
      try{await onSaved();}catch{if(alive.current)setError('Die Verifizierung wurde gespeichert. Bitte lade das Benutzerprofil neu, um den aktuellen Status zu sehen.');}
    }catch(cause){if(alive.current)setError(cause instanceof Error?cause.message:'Speichern fehlgeschlagen. Bitte erneut versuchen.');}
    finally{if(alive.current)setBusy(false);}
  }
  return <section className="admin-panel p-6" aria-label="Verifizierungen">
    <h2 className="mb-4 text-lg font-semibold text-slate-900">Verifizierungen</h2>
    <div className="flex flex-wrap items-start justify-between gap-4">
      <p className="max-w-2xl text-sm text-slate-600">Hinterlege einen manuell geprüften Ausweis. Ein gültiger Führerschein bestätigt zusätzlich den Fahrerstatus.</p>
      {!user.deletedAt&&!open&&<button className="btn btn-primary" disabled={loading} onClick={()=>{setOpen(true);setError('');setSuccess('');}}>Manuelle Verifizierung hinzufügen</button>}
    </div>
    {loadError&&<div role="alert" className="mt-4 rounded-lg bg-red-50 p-4 text-sm text-red-800">{loadError} <button className="btn btn-ghost" onClick={()=>void load()}>Erneut laden</button></div>}
    {error&&<p role="alert" className="mt-4 rounded-lg bg-red-50 p-4 text-sm text-red-800">{error}</p>}
    {success&&<p role="status" className="mt-4 rounded-lg bg-green-50 p-4 text-sm text-green-800">{success}</p>}
    {open&&!user.deletedAt&&<form onSubmit={save} className="mt-6 space-y-5 rounded-xl border border-slate-200 bg-slate-50 p-5 sm:p-6">
      <h3 className="text-lg font-semibold text-slate-900">Manuelle Verifizierung</h3>
      <div className="grid gap-4 sm:grid-cols-2">
        <label className="block text-sm font-medium text-slate-700">Dokumenttyp
          <select className="form-control mt-2 w-full" value={documentType} disabled={busy} onChange={event=>setDocumentType(event.target.value as DocumentType)}>
            {Object.entries(documentLabels).map(([value,label])=><option key={value} value={value}>{label}</option>)}
          </select></label>
        <label className="block text-sm font-medium text-slate-700">Ablaufdatum
          <input className="form-control mt-2 w-full" type="date" required min={isoToday()} value={expirationDate} disabled={busy} onChange={event=>setExpirationDate(event.target.value)}/>
        </label>
      </div>
      <div className="flex flex-wrap gap-3 border-t border-slate-200 pt-5">
        <button className="btn btn-primary" type="submit" disabled={busy}>{busy?'Wird gespeichert …':'Verifizierung speichern'}</button>
        <button className="btn btn-neutral" type="button" disabled={busy} onClick={()=>setOpen(false)}>Abbrechen</button>
      </div>
    </form>}
    {loading&&<p role="status" className="mt-5 text-sm text-slate-500">Verifizierungen werden geladen …</p>}
    {!loading&&!documents.length&&!loadError&&<p className="mt-5 text-sm text-slate-500">Noch keine Verifizierungen vorhanden.</p>}
    <div className="mt-6 grid gap-4 lg:grid-cols-2">
      {documents.map(document=>{
        const valid=document.status.toLowerCase()==='approved'&&(!document.expirationDate||document.expirationDate>=isoToday());
        return <article key={document.id} className="rounded-xl border border-slate-200 bg-white p-5">
          <header className="flex flex-wrap items-start justify-between gap-3"><div><h3 className="font-semibold text-slate-900">{document.documentType}</h3>
            <p className="mt-1 text-sm text-slate-500">{document.provider==='MANUAL'?'Manuell geprüft':document.provider}</p></div>
            <StatusBadge tone={valid?'green':'red'}>{valid?'Gültig':'Nicht gültig'}</StatusBadge></header>
          <p className="mt-3 text-sm text-slate-600">{document.expirationDate?`Gültig bis ${document.expirationDate}`:'Kein Ablaufdatum hinterlegt'}</p>
          {document.recordedBy&&<footer className="mt-4 border-t border-slate-100 pt-3 text-xs text-slate-500">Erfasst durch <span className="break-all">{document.recordedBy}</span>{document.recordedAt&&` · ${new Date(document.recordedAt).toLocaleString('de-AT',{timeZone:'Europe/Vienna'})}`}</footer>}
        </article>;
      })}
    </div>
  </section>;
}
